import { createContext, useContext, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";
import {
  useBancosConvenio,
  type BancoConvenio,
} from "@/hooks/usePortalBancos/useBancosConvenio";
import { useVistasDelRol } from "@/hooks/usePortalBancos/useVistasDelRol";
import { BROKER_PATH } from "@/hooks/usePortalBancos/useBrokerBancario";
import type { PersonaVerComo } from "@/lib/verComoPersonas";
import { usePagePermissions } from "@/hooks/usePagePermissions";

/**
 * Scope del Portal Bancos ("Ver como").
 *
 * "Ver como" es por PERSONA (regla de Eduardo, 2026-10-27): quien puede impersonar elige a una
 * persona con acceso al portal (`get_ver_como_personas`) y ve EXACTAMENTE lo que ella ve: las RPC
 * de lectura reciben su `p_auth_user_id` (mecanismo global `lectura_uid` de la BD) y el alcance
 * (su banco, el canal broker, todo o nada) se calcula como para ella. Antes se impersonaba un
 * alcance ("Broker bancario", un banco) con un usuario representante: dos personas del mismo
 * banco o del broker se veían igual aunque no lo fueran.
 *
 * Sin persona elegida, quien puede impersonar ve todo ("Super Admin"). Los usuarios con banco
 * vinculado (usuarios.id_banco) quedan siempre en su banco; los que tienen el submenú Broker y
 * ningún banco, en el canal broker. Todo queda en solo lectura mientras se impersona; las
 * acciones siempre van con los permisos del rol real.
 */

interface BankScopeContextType {
  /** Persona elegida en "Ver como"; null = vista propia. */
  persona: PersonaVerComo | null;
  setPersona: (p: PersonaVerComo | null) => void;
  /** Usuario sin banco ni impersonación con lectura del submenú Broker. */
  esBroker: boolean;
  /** Alcance de la persona impersonada (null sin "Ver como" o mientras se resuelve). */
  alcancePersona: BancoResolvedScope | null;
}

const STORAGE_KEY = "sozu-portal-bancos-ver-como-persona";
/** Llave anterior (alcance "all" / banco / "broker"); se limpia al cargar. */
const STORAGE_KEY_ANTERIOR = "sozu-portal-bancos-selected";

const BankScopeContext = createContext<BankScopeContextType>({
  persona: null,
  setPersona: () => {},
  esBroker: false,
  alcancePersona: null,
});

function leerPersonaGuardada(): PersonaVerComo | null {
  if (typeof window === "undefined") return null;
  try {
    window.localStorage.removeItem(STORAGE_KEY_ANTERIOR);
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const p = raw ? (JSON.parse(raw) as PersonaVerComo) : null;
    return p?.authUserId && p?.email ? p : null;
  } catch {
    return null;
  }
}

export function BankImpersonationProvider({ children }: { children: ReactNode }) {
  const [personaElegida, setPersonaState] = useState<PersonaVerComo | null>(leerPersonaGuardada);

  // ¿El usuario es del broker? Se resuelve una vez aquí (las vistas del rol van
  // en caché) y no en cada pantalla que pregunta por el alcance.
  const { profile } = useAuth();
  const puedeImpersonar = profile?.puede_impersonar === true;
  const sinBanco = !!profile && !puedeImpersonar && profile.id_banco == null;
  const { data: vistasPropias } = useVistasDelRol(sinBanco ? profile?.rol_id ?? null : null);
  const esBroker = sinBanco && !!vistasPropias?.has(BROKER_PATH);

  // Solo quien puede impersonar tiene persona elegida.
  const persona = puedeImpersonar ? personaElegida : null;

  // Alcance de la persona, con la misma regla que se aplica a ella al entrar: su rol puede
  // impersonar → todo; tiene banco → su banco; su rol ve la vista Broker → broker; si no, nada.
  const rolPersona = persona?.rolAccesoId ?? null;
  const { data: vistasPersona } = useVistasDelRol(rolPersona);
  const { data: rolPuedeImpersonar } = useQuery({
    queryKey: ["rol-puede-impersonar", rolPersona],
    enabled: rolPersona != null,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data } = await (supabase as any).from("roles").select("puede_impersonar").eq("id", rolPersona).maybeSingle();
      return data?.puede_impersonar === true;
    },
  });
  let alcancePersona: BancoResolvedScope | null = null;
  if (persona && rolPuedeImpersonar !== undefined && (persona.idBanco != null || vistasPersona)) {
    alcancePersona = rolPuedeImpersonar
      ? { kind: "all" }
      : persona.idBanco != null
        ? { kind: "banco", id: persona.idBanco }
        : vistasPersona?.has(BROKER_PATH)
          ? { kind: "broker" }
          : { kind: "none" };
  }

  usePublishImpersonationTarget(
    persona
      ? {
          email: persona.email,
          personaId: persona.personaId,
          nombre: persona.nombre,
          rolId: persona.rolAccesoId,
          rolNombre: persona.rolAccesoNombre,
          // Las lecturas del servidor responden como la persona (header `x-ver-como`).
          authUserId: persona.authUserId,
        }
      : null
  );

  const setPersona = (p: PersonaVerComo | null) => {
    setPersonaState(p);
    try {
      if (p == null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch {
      /* noop */
    }
  };

  return (
    <BankScopeContext.Provider value={{ persona, setPersona, esBroker, alcancePersona }}>
      {children}
    </BankScopeContext.Provider>
  );
}

export function useBancoScope() {
  return useContext(BankScopeContext);
}

/**
 * "Ver como" activo: quien puede impersonar eligió a una persona.
 * `verComoBanco` / `verComoBroker` son el alcance de esa persona (parámetros de lectura para el
 * servidor); `soloLectura` apaga toda escritura en la UI.
 */
export interface BancosVerComo {
  soloLectura: boolean;
  verComoBanco: number | null;
  verComoBroker: boolean;
}

export function useBancosVerComo(): BancosVerComo {
  const { persona, alcancePersona } = useBancoScope();
  return {
    soloLectura: persona != null,
    verComoBanco: alcancePersona?.kind === "banco" ? alcancePersona.id : null,
    verComoBroker: alcancePersona?.kind === "broker",
  };
}

/**
 * Usuario con el que se "ve como" (parámetro `p_auth_user_id` de las RPC de lectura): la
 * persona elegida. `null` sin "ver como".
 */
export function useVerComoAuthUserId(): string | null | undefined {
  const { persona } = useBancoScope();
  return persona?.authUserId ?? null;
}

/**
 * Super Administrador "de verdad" en la vista actual: rol real 1 y SIN "Ver como"
 * (ni banco ni broker). Al impersonar se ve lo que ve el usuario visto, sin las
 * pestañas ni acciones del Super Administrador. Fuente única: no usar
 * `profile.rol_id === 1` suelto en el Portal Bancos.
 */
export function useEsSuperAdminVista(): boolean {
  const { profile } = useAuth();
  const { soloLectura } = useBancosVerComo();
  return profile?.rol_id === 1 && !soloLectura;
}

/**
 * Nombre del rol que se muestra en el encabezado: el de la persona vista al impersonar (el rol
 * con el que entra al portal, p. ej. "Supervisor Broker"), el propio en otro caso.
 */
export function useRolNombreVista(): string | null {
  const { profile } = useAuth();
  const { persona } = useBancoScope();
  return persona ? persona.rolAccesoNombre : profile?.rol_nombre ?? null;
}

/**
 * Permisos de una vista del Portal Bancos con el rol real, y sin escritura
 * mientras se impersona (la lectura no cambia).
 */
export function usePermisosPortalBancos(path: string) {
  const permisos = usePagePermissions(path);
  const { soloLectura } = useBancosVerComo();
  if (!soloLectura) return permisos;
  return {
    ...permisos,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    canApprove: false,
    canChangeEmail: false,
    canDeriveToBank: false,
  };
}

export type BancoResolvedScope =
  | { kind: "all" }
  | { kind: "banco"; id: number }
  | { kind: "broker" }
  | { kind: "none" };

/**
 * Scope resuelto:
 * - Usuario con banco vinculado (usuarios.id_banco) y SIN impersonar → su banco.
 * - Usuario SIN banco vinculado y sin permiso de impersonar → `none`: no ve
 *   nada. Antes caía al "all" del Super Admin, así que un usuario de banco al
 *   que se le olvidó poner `id_banco` terminaba viendo las solicitudes de TODOS
 *   los bancos.
 * - Usuario SIN banco con lectura del submenú Broker → `broker`.
 * - Rol con puede_impersonar (Super Admin) → sin "Ver como", "all" (ver todo); con una persona
 *   elegida, el alcance de esa persona.
 */
export function useBancoResolvedScope(): BancoResolvedScope {
  const { profile } = useAuth();
  const { persona, esBroker, alcancePersona } = useBancoScope();
  const canImpersonate = profile?.puede_impersonar === true;

  if (!canImpersonate) {
    if (profile?.id_banco != null) return { kind: "banco", id: profile.id_banco };
    return esBroker ? { kind: "broker" } : { kind: "none" };
  }
  // Viendo como una persona: su alcance. Mientras se resuelve, nada (no se pinta de más).
  if (persona) return alcancePersona ?? { kind: "none" };
  return { kind: "all" };
}

/**
 * Banco con convenio actualmente en scope, o `null` si es la vista global
 * ("all" / Super Administrador). Devuelve `null` también mientras no cargan
 * los convenios.
 */
export function useCurrentBanco(): BancoConvenio | null {
  const scope = useBancoResolvedScope();
  const { data: convenios = [] } = useBancosConvenio();
  if (scope.kind !== "banco") return null;
  return convenios.find((b) => b.id_banco === scope.id) ?? null;
}

/**
 * Scope para las queries de solicitudes: id de banco (un banco), `"all"`
 * (todos los bancos, vista Super Administrador), `"broker"` (canal broker) o
 * `null` (sin banco asignado → la query queda deshabilitada y no se trae nada).
 */
export function useSolicitudScope(): number | "all" | "broker" | null {
  const scope = useBancoResolvedScope();
  if (scope.kind === "all") return "all";
  if (scope.kind === "broker") return "broker";
  if (scope.kind === "banco") return scope.id;
  return null;
}
